// IE Hub - Profile Dialog Component
// Full-page dialog on mobile, dropdown on desktop showing user profile and account options

import {
  Box,
  CloseIcon,
  Drawer,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme
} from '../ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_VERSION_INFO } from '../shared/version';
import {
  AccountCircleOutlined as AccountCircleIcon,
  AccountBalanceOutlined as AccountBalanceIcon,
  HeadsetMic as SupportAgentIcon,
  Email as CallIcon,
  Logout as LogoutIcon,
  HelpOutlineOutlined as HelpOutlineIcon
} from '@mui/icons-material';
import { RiBankLine as BankIcon } from "react-icons/ri";

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
  userName: string;
  userEmail?: string | undefined;
  onLogout: () => void;
  onContactUsClick?: (() => void) | undefined;
  onDeleteAccountClick?: (() => void) | undefined;
  /**
   * When true, hide all navigation options (except Logout) to enforce the
   * creditor Open Banking-only experience.
   */
  openBankingOnly?: boolean | undefined;
  anchorEl?: HTMLElement | null | undefined;
}

interface MenuItem {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  color?: string;
}

const ProfileDialog: React.FC<ProfileDialogProps> = ({ 
  open, 
  onClose, 
  onLogout,
  onContactUsClick,
  onDeleteAccountClick,
  openBankingOnly,
  anchorEl 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    const currentPath = location.pathname;

    // If the user is on budget or documents, request those pages to save
    // before navigating away (mirrors header behaviour).
    if (typeof window !== "undefined") {
      if (currentPath === "/update-budget") {
        window.dispatchEvent(
          new CustomEvent("iehub:budget-before-leave", {
            detail: { targetPath: path },
          })
        );
        onClose();
        return;
      }

      if (currentPath === "/documents") {
        window.dispatchEvent(
          new CustomEvent("iehub:documents-before-leave", {
            detail: { targetPath: path },
          })
        );
        onClose();
        return;
      }
    }

    navigate(path);
    onClose();
  };

  const menuItems: MenuItem[] = openBankingOnly
    ? []
    : [
        { 
          icon: <AccountCircleIcon sx={{ fontSize: 18 }} />, 
          label: 'My profile', 
          onClick: () => handleNavigation('/profile') 
        },
        { 
          icon: <BankIcon size={18} />, 
          label: 'Open banking service', 
          onClick: () => handleNavigation('/open-banking') 
        },
        { 
          icon: <SupportAgentIcon sx={{ fontSize: 18 }} />, 
          label: 'Help and support', 
          onClick: () => handleNavigation('/help-and-support') 
        },
        { 
          icon: <CallIcon sx={{ fontSize: 18 }} />, 
          label: 'Contact us', 
          onClick: () => {
            onClose();
            if (onContactUsClick) {
              onContactUsClick();
            }
          }
        },
        { 
          icon: <HelpOutlineIcon sx={{ fontSize: 18 }} />, 
          label: "FAQ's", 
          onClick: () => handleNavigation('/faqs') 
        },
      ];

  // Profile content component (drawer content)
  const drawerContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Close Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: 2,
          pb: 1
        }}
      >
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: '14.724px' }} />
        </IconButton>
      </Box>

      {/* Menu Items Container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          px: 2,
          pb: 3
        }}
      >
        {/* Top Menu Items */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {menuItems.map((item) => (
            <Box
              key={item.label}
              onClick={() => {
                item.onClick();
                onClose();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                cursor: 'pointer',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Box sx={{ color: item.color || '#000', display: 'flex', alignItems: 'center' }}>
                {item.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#000',
                  letterSpacing: '-0.304px',
                  lineHeight: '22.77px'
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Bottom Section: Logout + Version */}
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
          {/* Logout Button */}
          <Box
            onClick={onLogout}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              cursor: 'pointer',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(239, 83, 80, 0.08)'
              }
            }}
          >
            <Box sx={{ color: '#EF5350', display: 'flex', alignItems: 'center' }}>
              <LogoutIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#EF5350',
                letterSpacing: '-0.304px',
                lineHeight: '22.77px'
              }}
            >
              Logout
            </Typography>
          </Box>

          {/* Version Information */}
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                color: '#757575',
                letterSpacing: '-0.2px'
              }}
            >
              Version {APP_VERSION_INFO.version}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Mobile: Drawer from right
  if (isMobile) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            backgroundColor: '#ffffff',
            boxShadow: 'none',
          }
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: Dropdown/Popover
  return (
    <Popover
      open={open}
      anchorEl={anchorEl || null}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        mt: 1,
        '& .MuiPopover-paper': {
          width: 320,
          maxWidth: '90vw',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #cacaca'
        }
      }}
    >
      {drawerContent}
    </Popover>
  );
};

export default ProfileDialog;
